import { Box, Button, Stack } from '@mui/material'
import { forwardRef } from 'react'
import { IconButton, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { FullscreenModal } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { tLabel, useForm, UseFormProps, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'

export type ConsoleTextLinkEditProps = {
  open: boolean
  onClose: () => void
  artistId: string
  onSubmit: (values: TextLinkValues) => void
}

type TextLinkValues = {
  uri: string
  content: string
}

const schema: UseFormProps['schema'] = o => ({
  uri: o.string().uri().label(tLabel('consoleMyProfileEdit.uri')),
  content: o.string().max(20).label(tLabel('consoleMyProfileEdit.content')),
})

export const ConsoleTextLinkEdit: Organism<ConsoleTextLinkEditProps> = ({
  open,
  artistId,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation('consoleMyProfileEdit')
  const {
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    reset,
    register,
  } = useForm<TextLinkValues>({
    schema,
    defaultValues: { uri: '', content: '' },
  })

  const onHandleSubmit = handleSubmit(values => {
    onSubmit(values)
    reset()
    onClose()
  })

  return (
    <FullscreenModal open={open} onClose={() => onClose()}>
      <ArtistThemeProvider artistId={artistId}>
        <ConsoleBaseLayout
          margin
          header={{
            left: (
              <IconButton.Back
                edge="start"
                size="medium"
                onClick={onClose}
                color="white"
              />
            ),
            title: t('textLinkEditTitle'),
          }}
        >
          <Box sx={{ py: 5 }}>
            <TextInputCell
              type="text"
              {...register('content')}
              error={errors.content}
            />
            <TextInputCell
              type="text"
              {...register('uri')}
              error={errors.uri}
            />
            <Stack alignItems="center" sx={{ pt: 7 }}>
              <Button
                loading={isSubmitting}
                size="large"
                sx={{ width: 244 }}
                disabled={!isValid || isSubmitting}
                onClick={onHandleSubmit}
              >
                {t('register')}
              </Button>
            </Stack>
          </Box>
        </ConsoleBaseLayout>
      </ArtistThemeProvider>
    </FullscreenModal>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consoleMyProfileEdit')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)
